// modules/dashboard/utils/agregaciones.js
//
// Mismo cálculo que hicimos en el dashboard HTML (conteos simples, conteos
// de campos multi-selección, promedios de edad con outliers excluidos),
// pero acá se lee directo de lo que devuelven las APIs reales — no de un
// Excel exportado. Diferencias importantes con el Excel:
//   - Los campos JSON (grupo_poblacional, necesidades_principales, etc.)
//     ya vienen como array de JS, no como texto separado por "|".
//   - Los nombres de columna son los reales del modelo (snake_case), no
//     los encabezados bonitos en español del Excel.
//   - RBC/Discapacidad es distinto a los otros 3: sus categorías y
//     dificultades viven en TABLAS RELACIONADAS (vía include de
//     Sequelize), no en una columna JSON — por eso necesita su propio
//     lector de asociaciones en vez de contarMultiple().

// "LETICIA 01" -> "LETICIA", "PUERTO NARIÑO 02" -> "PUERTO NARIÑO"
export function unificarTerritorio(m) {
  if (!m) return null;
  return String(m).trim().replace(/\s+\d+$/, "");
}

export function contarSimple(lista, campo, normalizeMap) {
  const c = {};
  lista.forEach((p) => {
    let v = p[campo];
    if (v === null || v === undefined || v === "") return;
    if (typeof v === "boolean") v = v ? "true" : "false";
    v = String(v).trim();
    if (normalizeMap) v = normalizeMap[v] || normalizeMap[v.toLowerCase()] || v;
    c[v] = (c[v] || 0) + 1;
  });
  return c;
}

// Para campos JSON tipo array (grupo_poblacional, necesidades_principales...)
export function contarMultiple(lista, campo) {
  const c = {};
  lista.forEach((p) => {
    const arr = p[campo];
    if (!Array.isArray(arr)) return;
    arr.forEach((tag) => {
      const t = String(tag).trim();
      if (!t) return;
      c[t] = (c[t] || 0) + 1;
    });
  });
  return c;
}

// Para las asociaciones de RBC (categorías, dificultades...) — cada
// persona trae un array de objetos {descripcion: "..."}, y Sequelize
// puede devolver el nombre de la asociación con distinta pluralización
// según el modelo — se prueban varios nombres posibles, igual que
// getAsoc() en TablaDiscapacidad.js.
export function contarAsociacion(lista, posiblesNombres) {
  const c = {};
  lista.forEach((p) => {
    let arr = null;
    for (const n of posiblesNombres) {
      if (Array.isArray(p[n]) && p[n].length) { arr = p[n]; break; }
    }
    if (!arr) return;
    arr.forEach((item) => {
      const desc = item?.descripcion;
      if (!desc) return;
      c[desc] = (c[desc] || 0) + 1;
    });
  });
  return c;
}

// Sí/No con todas sus variantes ("Si", "si", "SI", true, 1…). Importante:
// rlcpcd_certificado es BOOLEAN en la BD, así que llega como true/false — sin
// normalizar, el KPI de "con certificado" contaba "Sí" y siempre daba 0%.
export const SI_NO_NORM = {
  true: "Sí", false: "No", "1": "Sí", "0": "No",
  si: "Sí", "sí": "Sí", no: "No", "no sabe": "No sabe", "no aplica": "No aplica",
};

export const ETNIA_NORM = {
  ticuna: "Tikuna", tikuna: "Tikuna", cocama: "Cocama", yagua: "Yagua",
  mestizo: "Mestizo", ocaina: "Ocaina", huitoto: "Huitoto",
};
export const NIVEL_EDU_NORM = {
  primaria: "Primaria", "primaría": "Primaria", primeria: "Primaria", "primería": "Primaria",
  "quinto primaria": "Primaria",
  secundaria: "Secundaria", "secundaría": "Secundaria", bachiller: "Secundaria",
  ninguno: "Ninguno", ninguna: "Ninguno", no: "Ninguno",
  licenciada: "Profesional", profesional: "Profesional",
};

function edadesValidas(lista) {
  const edades = lista.map((p) => Number(p.edad_anios)).filter((e) => !isNaN(e) && e >= 0 && e <= 110);
  const outliers = lista.filter((p) => { const e = Number(p.edad_anios); return isNaN(e) || e < 0 || e > 110; });
  return {
    edadPromedio: edades.length ? Math.round((edades.reduce((a, b) => a + b, 0) / edades.length) * 10) / 10 : 0,
    edadMin: edades.length ? Math.min(...edades) : 0,
    edadMax: edades.length ? Math.max(...edades) : 0,
    outliersEdad: outliers.length,
  };
}

// Vejez / Migrante / Habitante de Calle — mismo Formato de Caracterización
// compartido, mismos nombres de campo en los 3 modelos.
export function calcularFormatoCompartido(lista) {
  return {
    total: lista.length,
    ...edadesValidas(lista),
    genero: contarSimple(lista, "genero"),
    comunidad: contarSimple(lista, "nombre_comunidad"),
    estadoCivil: contarSimple(lista, "estado_civil"),
    grupoPoblacional: contarMultiple(lista, "grupo_poblacional"),
    pertenenciaDiferencial: contarMultiple(lista, "pertenencia_diferencial"),
    etnia: contarSimple(lista, "etnia", ETNIA_NORM),
    enfermedadesCronicas: contarSimple(lista, "enfermedades_cronicas", SI_NO_NORM),
    vacunacion: contarSimple(lista, "vacunacion_al_dia", SI_NO_NORM),
    estadoFuncional: contarSimple(lista, "estado_funcional"),
    estadoAnimo: contarSimple(lista, "estado_animo"),
    tipoVivienda: contarSimple(lista, "tipo_vivienda"),
    ingresos: contarSimple(lista, "ingresos"),
    redApoyo: contarMultiple(lista, "red_apoyo"),
    necesidades: contarMultiple(lista, "necesidades_principales"),
    barreras: contarMultiple(lista, "barreras_identificadas"),
    nivelEducativo: contarSimple(lista, "nivel_educativo", NIVEL_EDU_NORM),
    gestor: contarSimple(lista, "gestor"),
  };
}

// RBC/Discapacidad — estructura propia (campos distintos + asociaciones).
export function calcularRBC(lista) {
  return {
    total: lista.length,
    ...edadesValidas(lista),
    sexo: contarSimple(lista, "sexo"),
    comunidad: contarSimple(lista, "nombre_comunidad"),
    eps: contarSimple(lista, "eps"),
    culturaIndigena: contarSimple(lista, "cultura_indigena", ETNIA_NORM),
    categorias: contarAsociacion(lista, ["categorias_discapacidades", "CategoriaDiscapacidads", "CategoriaDiscapacidades", "CategoriaDiscapacidad"]),
    dificultades: contarAsociacion(lista, ["dificultades_diarias", "Dificultads", "Dificultades"]),
    sabeLeer: contarSimple(lista, "educacion_sabe_leer", SI_NO_NORM),
    asisteEducacion: contarSimple(lista, "educacion_asiste", SI_NO_NORM),
    trabajando: contarSimple(lista, "trabajo_actualmente", SI_NO_NORM),
    certificado: contarSimple(lista, "rlcpcd_certificado", SI_NO_NORM),
    requiereCuidado: contarSimple(lista, "cuidado_requiere_ayuda", SI_NO_NORM),
    utilizaPaniales: contarSimple(lista, "utiliza_paniales", SI_NO_NORM),
    gestor: contarSimple(lista, "gestor"),
  };
}

// Conteo por comunidad de las 4 estrategias a la vez, para la sección comparativa.
export function calcularComparativoComunidades(porEstrategia) {
  // porEstrategia: { rbc: [...], vejez: [...], migrante: [...], habitante_calle: [...] }
  const todasComunidades = new Set();
  Object.values(porEstrategia).forEach((lista) => {
    lista.forEach((p) => { if (p.nombre_comunidad) todasComunidades.add(String(p.nombre_comunidad).trim()); });
  });

  return Array.from(todasComunidades).sort().map((comunidad) => {
    const fila = { comunidad };
    Object.entries(porEstrategia).forEach(([nombre, lista]) => {
      fila[nombre] = lista.filter((p) => String(p.nombre_comunidad || "").trim() === comunidad).length;
    });
    return fila;
  });
}
